import { elakay_API } from "../../config";
import { isAuthenticated } from "../auth";
import { uploadFileRequest } from "../../utils/uploadRequest";

const getToken = () => isAuthenticated()?.token || "";

const buildQuery = (params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      query.set(key, value);
    }
  });
  return query.toString();
};

const apiFetch = async (path, { method = "GET", body, token } = {}) => {
  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };

  const resolvedToken = token || getToken();
  if (resolvedToken) headers.Authorization = `Bearer ${resolvedToken}`;

  const response = await fetch(`${elakay_API}${path}`, {
    method,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.error || `Erreur jobs ${response.status}`);
    error.status = response.status;
    throw error;
  }
  return data;
};

// `purpose: "application"` stores the file in the protected zone (CVs and
// other job-application files, readable through signed links only).
const uploadJobFile = async (file, token, { purpose } = {}) => {
  const headers = {
    Accept: "application/json",
  };
  const resolvedToken = token || getToken();
  if (resolvedToken) headers.Authorization = `Bearer ${resolvedToken}`;

  return uploadFileRequest({
    url: `${elakay_API}/documents/upload${purpose ? `?purpose=${encodeURIComponent(purpose)}` : ""}`,
    feature: "document",
    file,
    headers,
    throwOnError: true,
  });
};

export const uploadJobDocument = uploadJobFile;

export const uploadJobImage = uploadJobFile;

// Candidate files (CV, cover letter, ID...): protected zone + signed link.
export const uploadJobApplicationDocument = (file, token) => uploadJobFile(file, token, { purpose: "application" });

export const getJobPostings = (params = {}, token) => {
  const query = buildQuery(params);
  return apiFetch(`/jobs/public${query ? `?${query}` : ""}`, { token });
};

export const getJobPosting = (slugOrId, token) =>
  apiFetch(`/jobs/public/${encodeURIComponent(slugOrId)}`, { token });

export const applyToJob = (jobId, payload, token) =>
  apiFetch(`/jobs/${encodeURIComponent(jobId)}/apply`, {
    method: "POST",
    body: payload,
    token,
  });

export const getMyJobApplications = (params = {}, token) => {
  const query = buildQuery(params);
  return apiFetch(`/jobs/user/applications${query ? `?${query}` : ""}`, { token });
};

// Self-service view/edit of a single application, used by /my-job-applications
// (View Application / Edit Application). Ownership + edit-lock is enforced
// server-side; these just surface the API.
export const getJobApplicationById = (applicationId, token) =>
  apiFetch(`/jobs/applications/${encodeURIComponent(applicationId)}`, { token });

export const updateJobApplication = (applicationId, payload, token) =>
  apiFetch(`/jobs/applications/${encodeURIComponent(applicationId)}`, {
    method: "PATCH",
    body: payload,
    token,
  });

export const createJobPosting = (payload, token) =>
  apiFetch("/jobs/admin/jobs", {
    method: "POST",
    body: payload,
    token,
  });

export const getAdminJobPostings = (params = {}, token) => {
  const query = buildQuery(params);
  return apiFetch(`/jobs/admin/jobs${query ? `?${query}` : ""}`, { token });
};

export const updateJobPosting = (id, payload, token) =>
  apiFetch(`/jobs/admin/jobs/${id}`, {
    method: "PATCH",
    body: payload,
    token,
  });

export const changeJobPostingStatus = (id, payload, token) =>
  apiFetch(`/jobs/admin/jobs/${id}/status`, {
    method: "PATCH",
    body: payload,
    token,
  });

export const getJobApplications = (jobId, params = {}, token) => {
  const query = buildQuery(params);
  return apiFetch(`/jobs/admin/jobs/${jobId}/applications${query ? `?${query}` : ""}`, {
    token,
  });
};

export const getAllJobApplications = (params = {}, token) => {
  const query = buildQuery(params);
  return apiFetch(`/jobs/admin/applications${query ? `?${query}` : ""}`, {
    token,
  });
};

export const approveJobApplication = (applicationId, capacities, token) =>
  apiFetch(`/jobs/admin/applications/${encodeURIComponent(applicationId)}/approve`, {
    method: "PATCH",
    body: { capacities },
    token,
  });
